import {
  MarketTypeEnum,
  useMarketAccountsContext,
  useMarketsContext,
  type ConnectionType,
  type Market,
  type MarketAccount,
} from '@talos/kyoko';
import { useCallback } from 'react';

interface SecurityMarketsProps {
  markets: string[];
  showInactiveMarketAccounts: boolean;
}

export const useSecurityMarkets = ({ markets, showInactiveMarketAccounts }: SecurityMarketsProps) => {
  const { marketAccountsByMarket, isMarketAccountActive } = useMarketAccountsContext();
  const { marketsByName, isMarketConfigured } = useMarketsContext();

  const isMaActive = useCallback(
    (marketAccount: MarketAccount) => (showInactiveMarketAccounts ? () => true : isMarketAccountActive(marketAccount)),
    [showInactiveMarketAccounts, isMarketAccountActive]
  );

  /**
   * Get all available Markets for Security with specified connectionType
   * @returns array of all active&configured Markets that support specified connectionType
   * @param connectionType - Only markets with this connectionType support will be returned
   */
  const getSecurityMarkets = useCallback(
    (connectionType: ConnectionType): Market[] => {
      return markets
        .reduce((acc: Market[], marketName) => {
          const marketAccounts = marketAccountsByMarket.get(marketName);
          const availableMarketsAccounts =
            marketAccounts?.filter(ma => {
              return isMaActive(ma) && isMarketConfigured(ma.Market, connectionType);
            }) ?? [];

          if (availableMarketsAccounts.length > 0) {
            const market = marketsByName.get(availableMarketsAccounts[0].Market)!;
            acc.push(market);
          } else {
            const market = marketsByName.get(marketName);
            if (
              market &&
              isMarketConfigured(market, connectionType) &&
              [MarketTypeEnum.Exchange, MarketTypeEnum.Dealer].some(type => type === market?.Type)
            ) {
              const marketAccounts = marketAccountsByMarket?.get(market.Name);
              if (!marketAccounts || (marketAccounts.length > 0 && marketAccounts.some(ma => isMaActive(ma)))) {
                acc.push(market);
              }
            }
          }
          return acc;
        }, [])
        .toSorted((a, b) => (a && b && a.DisplayName.localeCompare(b.DisplayName)) || 0);
    },
    [markets, isMarketConfigured, marketAccountsByMarket, marketsByName, isMaActive]
  );

  return {
    getSecurityMarkets,
  };
};
